import { useState, type ReactNode } from 'react';
import { AudioPlayButton, formatAudioDuration } from './AudioPlayButton';
import styles from './AudioTrackTable.module.css';

/**
 * 音声を選ぶモーダル (フリー楽曲 / 効果音 / SEカタログ) で共通に使う一覧テーブル。
 *
 * 見た目は音楽ストアの曲一覧に揃える: 見出し行 + 罫線区切りの 1 行ずつの低い行で、
 * 数十件を 1 画面で見渡せるようにする。左端には全行に試聴ボタンを常に置き (行番号は出さない)、
 * 再生中の行は曲名をアクセント色にする。出どころ (パートナー独自 / 共通ライブラリ) は見出しで区切らず、
 * アルバム列のように「ライブラリ」列で各行に出す。
 * 選ぶ操作は行のクリック (キーボードは Enter / Space)。「選択」ボタンは置かず、右端には
 * 「選択中」「設定中…」などの状態だけを小さく出す。試聴ボタンのクリックは選択にしない。
 * オーバーレイ / ヘッダーなどモーダルの枠は各呼び出し側が modal-common.css の共通クラスで組む。
 */

export interface AudioTrackRow {
  id: string;
  name: string;
  /** 名前の後ろに薄く続ける補足 (説明文など)。行を低く保つため 2 行にはしない。 */
  subText?: string | null;
  /** 出どころ (「〇〇オリジナル」「共通ライブラリ」など)。どの行にも無ければ列ごと出さない。 */
  library?: string | null;
  /** 用途などの短いラベル。どの行にも無ければ列ごと出さない。 */
  badge?: string | null;
  /** 生成元のシナリオ名 (ナレーション)。長ければ省略して出し、全文はツールチップ。どの行にも無ければ列ごと出さない。 */
  scenario?: string | null;
  durationSeconds: number | null | undefined;
  /** 試聴 URL。無い行は試聴できず、名前を薄く出す。 */
  previewUrl?: string | null;
  /** 採用中 / 追加済みの行 (アクセント色で縁取る)。 */
  selected?: boolean;
  /** 右端に出す状態 (「選択中」「設定中…」「追加済み」など)。 */
  status?: string | null;
  /** 行を選べないとき。理由は disabledReason をツールチップに出す。 */
  disabled?: boolean;
  disabledReason?: string;
  onSelect: () => void;
}

interface AudioTrackTableProps {
  rows: AudioTrackRow[];
  /** 名前列の見出し (既定「名前」。楽曲なら「曲名」)。 */
  nameLabel?: string;
  libraryLabel?: string;
  badgeLabel?: string;
  scenarioLabel?: string;
  /** 各行の data-testid。 */
  rowTestId?: string;
}

export function AudioTrackTable({
  rows,
  nameLabel = '名前',
  libraryLabel = 'ライブラリ',
  badgeLabel = '用途',
  scenarioLabel = 'シナリオ',
  rowTestId,
}: AudioTrackTableProps) {
  const showLibrary = rows.some((r) => !!r.library);
  const showBadge = rows.some((r) => !!r.badge);
  const showScenario = rows.some((r) => !!r.scenario);

  return (
    <table className={styles.table}>
      <colgroup>
        <col className={styles.colPlay} />
        <col />
        {showScenario && <col className={styles.colScenario} />}
        {showLibrary && <col className={styles.colLibrary} />}
        {showBadge && <col className={styles.colBadge} />}
        <col className={styles.colDuration} />
        <col className={styles.colStatus} />
      </colgroup>
      <thead>
        <tr>
          <th scope="col" className={styles.playCell} aria-label="試聴" />
          <th scope="col">{nameLabel}</th>
          {showScenario && <th scope="col">{scenarioLabel}</th>}
          {showLibrary && <th scope="col">{libraryLabel}</th>}
          {showBadge && <th scope="col">{badgeLabel}</th>}
          <th scope="col" className={styles.durationCell}>時間</th>
          <th scope="col" aria-label="状態" />
        </tr>
      </thead>
      <tbody>
        {rows.map((row) => (
          <AudioTrackTableRow
            key={row.id}
            row={row}
            showLibrary={showLibrary}
            showBadge={showBadge}
            showScenario={showScenario}
            testId={rowTestId}
          />
        ))}
      </tbody>
    </table>
  );
}

function AudioTrackTableRow({
  row,
  showLibrary,
  showBadge,
  showScenario,
  testId,
}: {
  row: AudioTrackRow;
  showLibrary: boolean;
  showBadge: boolean;
  showScenario: boolean;
  testId?: string;
}) {
  const [playing, setPlaying] = useState(false);
  const playable = !!row.previewUrl;
  const className = [
    styles.row,
    row.selected ? styles.rowSelected : '',
    playing ? styles.rowPlaying : '',
    playable ? '' : styles.rowUnplayable,
    row.disabled ? styles.rowDisabled : '',
  ]
    .filter(Boolean)
    .join(' ');

  const select = () => {
    if (!row.disabled) row.onSelect();
  };

  return (
    <tr
      className={className}
      data-testid={testId}
      tabIndex={row.disabled ? -1 : 0}
      aria-current={row.selected ? 'true' : undefined}
      aria-disabled={row.disabled ? 'true' : undefined}
      title={row.disabled ? row.disabledReason : undefined}
      onClick={select}
      onKeyDown={(e) => {
        if (e.target !== e.currentTarget) return; // 試聴ボタン上の Enter / Space は試聴に任せる
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          select();
        }
      }}
    >
      {/* 試聴ボタンのクリックは行の選択に伝えない */}
      <td className={styles.playCell} onClick={(e) => e.stopPropagation()}>
        {playable ? (
          <AudioPlayButton src={row.previewUrl as string} onPlayingChange={setPlaying} />
        ) : (
          <span className={styles.playUnavailable} title="現在この音声は試聴できません">–</span>
        )}
      </td>
      <td title={row.disabled ? undefined : row.subText ? `${row.name}\n${row.subText}` : row.name}>
        <span className={styles.name}>{row.name}</span>
        {row.subText && <span className={styles.subText}>{row.subText}</span>}
      </td>
      {showScenario && (
        <td className={styles.libraryCell} title={row.scenario ?? undefined}>
          {row.scenario}
        </td>
      )}
      {showLibrary && (
        <td className={styles.libraryCell} title={row.library ?? undefined}>
          {row.library}
        </td>
      )}
      {showBadge && <td>{row.badge && <span className={styles.badge}>{row.badge}</span>}</td>}
      <td className={styles.durationCell}>{formatAudioDuration(row.durationSeconds)}</td>
      <td className={styles.statusCell}>{row.status}</td>
    </tr>
  );
}

/** 読み込み中 / 0 件 / 失敗などの案内文。テーブルの代わりに同じ余白で出す。 */
export function AudioTrackTableMessage({ children }: { children: ReactNode }) {
  return <p className={styles.message}>{children}</p>;
}
